import { Injectable } from '@angular/core';
import { AngularFireDatabase, AngularFireList } from '@angular/fire/compat/database';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { TranslocoService } from '@ngneat/transloco';
import { BehaviorSubject, filter, Observable, Subscription } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { NotificationsService } from '../+state/notifications.service';
import { LocalStorageService } from '@us/web/shared/data-access';
import * as NotificationsActions from '../+state/notifications.actions';
import { MessageService } from '@us/web/shared/feature';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { tuiIsPresent } from '@taiga-ui/cdk';
import { NotificationM } from '../models/notification-m';

/**
 * @deprecated
 */
@Injectable({
    providedIn: 'root'
})
export class MessagingService {
    private messagingToken: BehaviorSubject<string> = new BehaviorSubject<string>(
        this.localStorage.getItem('messaging_token')
    );
    public messagingToken$: Observable<string> = this.messagingToken.asObservable();

    // Using realtime database to noti
    notiListRef: AngularFireList<any> | undefined;

    notiSubscription: Subscription | undefined;
    fcmSubscription: Subscription | undefined;

    private notifications: BehaviorSubject<NotificationM | null> = new BehaviorSubject<NotificationM | null>(null);
    public notifications$ = this.notifications.asObservable();

    private notificationPermission: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public notificationPermission$: Observable<boolean> = this.notificationPermission.asObservable();

    constructor(
        private afMessaging: AngularFireMessaging,
        private db: AngularFireDatabase,
        private messageService: MessageService,
        private localStorage: LocalStorageService,
        private translocoService: TranslocoService,
        private store: Store,
        private notificationsService: NotificationsService,
        private afs: AngularFirestore
    ) {
        const value = this.getNotificationPermission();
        this.notificationPermission.next(value);
    }

    get notificationSupported() {
        return 'Notification' in window && 'ServiceWorkerRegistration' in window;
    }

    get notificationBlocked() {
        return Notification.permission === 'denied';
    }

    get notificationGranted() {
        return Notification.permission === 'granted';
    }

    getNotificationPermission() {
        return this.notificationSupported && Notification.permission === 'granted';
    }

    /** @deprecated */
    // requestMessagingToken(accountId: number | string, isLocalAccount = false): Observable<string | null> {
    // if (!this.notificationSupported || this.notificationBlocked) {
    //     return of(null);
    // }
    //
    // if (this.messagingToken.value && this.getNotificationPermission()) {
    //     return of(this.messagingToken.value);
    // }
    // const modal: NzModalRef = this.modalService.create({
    //     nzTitle: this.translocoService.translate('notification'),
    //     nzContent: this.translocoService.translate(
    //         'allows_sending_notifications_to_receive_latest_updates_from_your_order'
    //     ),
    //     nzFooter: [
    //         {
    //             label: this.translocoService.translate('skip'),
    //             onClick: () => modal.destroy(false)
    //         },
    //         {
    //             label: this.translocoService.translate('ok'),
    //             type: 'primary',
    //             onClick: () => modal.destroy(true)
    //         }
    //     ]
    // });
    //
    // return modal.afterClose.pipe(
    //     switchMap((result: boolean) => {
    //         if (result) {
    //             // const messaging = getMessaging();
    //             // return from(getToken(messaging, { vapidKey: environment.firebase.vapidKey }))
    //             return this.afMessaging.requestToken.pipe(
    //                 tap(token => {
    //                     if (token) {
    //                         if (!isLocalAccount) {
    //                             this.notificationsService
    //                                 .registerPushNotification(token, accountId)
    //                                 .subscribe(() => {});
    //                         }
    //                         this.localStorage.setItem('messaging_token', token);
    //                         this.messagingToken.next(token);
    //                     } else {
    //                         this.subscribeNotiList(accountId);
    //                     }
    //                 })
    //                 // catchError(error => {
    //                 //     console.log(error);
    //                 //     this.subscribeNotiList(accountId);
    //                 //     return of(null);
    //                 // })
    //             );
    //         }
    //         return of(null);
    //     })
    // );
    // }

    listen() {
        this.fcmSubscription = this.afMessaging.messages.subscribe(payload => {
            const ref = Number(payload.data?.['ref']);
            this.getNotificationFromServer(ref);
        });
    }

    getNotiList(accountId: number | string) {
        if (!accountId) {
            return null;
        }

        this.notiListRef = this.db.list(`/${accountId}`);
        return this.notiListRef;
    }

    subscribeNotiList(accountId: number | string) {
        // if (this.messagingToken.value && this.getNotificationPermission()) {
        //     // use FCM
        //     return;
        // }

        this.notiSubscription?.unsubscribe();
        //
        // this.notiSubscription = this.getNotiList(accountId)
        //     ?.valueChanges()
        //     .subscribe(res => {
        //         const previousNotiList = this.localStorage.getItem('noti_list');
        //         this.localStorage.setItem('noti_list', res);
        //
        //         if (JSON.stringify(previousNotiList) !== JSON.stringify(res)) {
        //             const [ref] = res;
        //             this.getNotificationFromServer(ref, true);
        //         }
        //     });

        this.notiSubscription = this.afs
            .collection(`users:${accountId}`)
            .doc<{ notification_id: number }>('latestNotification')
            .valueChanges()
            .pipe(
                filter(tuiIsPresent),
                // tap(value => console.log(value)),
                tap(({ notification_id }) => {
                    const previousNotiList = this.localStorage.getItem('noti_list');
                    this.localStorage.setItem('noti_list', [notification_id]);

                    if (JSON.stringify(previousNotiList) !== JSON.stringify([notification_id])) {
                        this.getNotificationFromServer(notification_id, true);
                    }
                })
            )
            .subscribe();
    }

    deleteMessagingToken() {
        this.afMessaging.getToken.pipe(mergeMap(token => this.afMessaging.deleteToken(token || ''))).subscribe(() => {
            console.log('Token deleted!');
        });
    }

    getNotificationFromServer(ref: number, maskAsRead = false) {
        if (!ref) {
            return;
        }
        this.notificationsService.getNotification(ref, maskAsRead).subscribe(
            result => {
                this.pushNotification(result);
            },
            error => {
                if (error.status !== 404) {
                    throw error;
                }
            }
        );
    }

    public pushNotification(value: NotificationM) {
        const { title, body } = value;
        this.notifications.next(value);
        this.messageService.info(body, title);
        this.store.dispatch(NotificationsActions.markNotificationAsRead({ id: value.id }));
    }
}
